import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Spin } from "antd";
import { CaretDown, CaretUp, Sparkle } from "@phosphor-icons/react";
import {
  fetchDocumentBriefing,
  selectBriefingForDocument,
} from "../../../slice/AISlice";
import BriefingMarkdown from "../../../components/AIPane/BriefingMarkdown";
import {
  BriefingBody,
  BriefingSection,
  BriefingStatusText,
  BriefingToggle,
  BriefingToggleLabel,
} from "../MobileInboxStyled";

/**
 * Collapsible AI-briefing strip for the mobile inbox detail screen
 * (Mobile DMS v1, Phase 2). Replaces the desktop 500px AI rail.
 *
 * Collapsed by default and lazy-fetches on first expand so rapid
 * mark-reviewed triage doesn't fire a briefing request per document.
 * Reads/writes the same `AISlice.briefings` cache as the desktop
 * `BriefingTab`, so a doc opened on both surfaces shares one fetch.
 *
 * Citation pills render but clicking is a no-op on mobile — the
 * lightweight viewer has no search-highlight surface (v1 cut).
 */
const MobileBriefingSection = ({ documentId = null }) => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const briefingState = useSelector((state) =>
    selectBriefingForDocument(state, documentId)
  );

  // Collapse when the user advances to another document so the next
  // doc's screen starts from the compact triage-first state.
  useEffect(() => {
    setExpanded(false);
  }, [documentId]);

  useEffect(() => {
    if (!expanded || !documentId) return;
    if (briefingState.status === "idle") {
      dispatch(fetchDocumentBriefing(documentId));
    }
    // `status` deliberately not a dep: `empty`/`failed` must not refire
    // in a loop — retry is the explicit button below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, documentId, dispatch]);

  const handleRetry = useCallback(() => {
    if (documentId) dispatch(fetchDocumentBriefing(documentId));
  }, [dispatch, documentId]);

  if (!documentId) return null;

  return (
    <BriefingSection aria-label="AI briefing">
      <BriefingToggle
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
      >
        <BriefingToggleLabel>
          <Sparkle size={15} weight="fill" />
          AI Briefing
        </BriefingToggleLabel>
        {expanded ? (
          <CaretUp size={14} weight="bold" />
        ) : (
          <CaretDown size={14} weight="bold" />
        )}
      </BriefingToggle>

      {expanded && (
        <BriefingBody>
          {(briefingState.status === "loading" ||
            briefingState.status === "idle") && (
            <BriefingStatusText>
              <Spin size="small" /> Loading briefing…
            </BriefingStatusText>
          )}
          {briefingState.status === "empty" && (
            <BriefingStatusText>
              Briefing not ready yet — the pipeline writes one automatically
              once processing finishes.{" "}
              <button
                type="button"
                onClick={handleRetry}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "#0C53BB",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Check again
              </button>
            </BriefingStatusText>
          )}
          {briefingState.status === "failed" && (
            <BriefingStatusText>
              {briefingState.error || "Couldn't load the briefing."}{" "}
              <button
                type="button"
                onClick={handleRetry}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  color: "#0C53BB",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Retry
              </button>
            </BriefingStatusText>
          )}
          {briefingState.status === "ready" && briefingState.data && (
            <BriefingMarkdown
              content={briefingState.data.briefing_text || ""}
            />
          )}
        </BriefingBody>
      )}
    </BriefingSection>
  );
};

MobileBriefingSection.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default MobileBriefingSection;
